import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  DollarSign,
  FileText,
  TrendingUp,
  BarChart2,
  HardHat,
  Banknote,
  CalendarDays,
  Truck,
  Wallet,
  Users,
  Wrench,
  ArrowRight,
  Shield,
} from "lucide-react";
import { ROUTES } from "@/config/constants";
import { cn } from "@/utils/cn";
import { useAppAccess } from "@/hooks/useAppAccess";
import { canAccessLocacoesModule, canAccessSolicitacaoPagamentoModule, isMarketingOnlyUser, isOperadorOnlyUser, type AppAccessKey } from "@/lib/appAccess";
import { useAuth } from "@/hooks/useAuth";

/* ─── Dados dos módulos ──────────────────────────────────────────────────── */

interface Module {
  id: string;
  label: string;
  description: string;
  Icon: React.ElementType;
  gradientFrom: string;
  gradientTo: string;
}

const MODULES: Module[] = [
  {
    id: "suprimentos",
    label: "Suprimentos",
    description: "Compras, estoque\ne fornecedores",
    Icon: ShoppingCart,
    gradientFrom: "#4a90e2",
    gradientTo: "#67d4f0",
  },
  {
    id: "financeiro",
    label: "Financeiro",
    description: "Fluxo de caixa,\ncontas e resultados",
    Icon: DollarSign,
    gradientFrom: "#27ae78",
    gradientTo: "#2ecc9a",
  },
  {
    id: "orcamentos",
    label: "Orçamentos",
    description: "Planejamento\ne controle orçamentário",
    Icon: FileText,
    gradientFrom: "#f5a623",
    gradientTo: "#fad056",
  },
  {
    id: "comercial",
    label: "Comercial",
    description: "Vendas, clientes\ne performance",
    Icon: TrendingUp,
    gradientFrom: "#8b5cf6",
    gradientTo: "#a78bfa",
  },
  {
    id: "gerencial",
    label: "Gerencial",
    description: "Indicadores estratégicos\ne inteligência de negócios",
    Icon: BarChart2,
    gradientFrom: "#e74c3c",
    gradientTo: "#f87171",
  },
  {
    id: "chaoDeObra",
    label: "Chão de Obra",
    description: "Fotos por ambiente\ne avanço físico em campo",
    Icon: HardHat,
    gradientFrom: "#b45309",
    gradientTo: "#fbbf24",
  },
  {
    id: "solicitacaoPagamento",
    label: "Solicitação de Pagamento",
    description: "Solicitar e aprovar\npagamentos",
    Icon: Banknote,
    gradientFrom: "#0d9488",
    gradientTo: "#5eead4",
  },
  {
    id: "tarefas",
    label: "Tarefas",
    description: "Calendário e gestão\nde tarefas por dia",
    Icon: CalendarDays,
    gradientFrom: "#6366f1",
    gradientTo: "#a5b4fc",
  },
  {
    id: "locacoes",
    label: "Semog Locações",
    description: "Maquinários, locações\ne manutenções",
    Icon: Truck,
    gradientFrom: "#ea580c",
    gradientTo: "#fdba74",
  },
  {
    id: "manutencao",
    label: "Manutenção",
    description: "Preventivas, corretivas\ne ordens de serviço",
    Icon: Wrench,
    gradientFrom: "#0369a1",
    gradientTo: "#38bdf8",
  },
  {
    id: "folhaPagamentos",
    label: "Folha de Pagamentos",
    description: "Folhas, colaboradores\ne relatórios",
    Icon: Wallet,
    gradientFrom: "#059669",
    gradientTo: "#34d399",
  },
  {
    id: "genteGestao",
    label: "Gente & Gestão",
    description: "Colaboradores, eNPS\ne avaliação de liderança",
    Icon: Users,
    gradientFrom: "#0f766e",
    gradientTo: "#5eead4",
  },
];

function moduleAccessKey(id: string): AppAccessKey | null {
  switch (id) {
    case "suprimentos":
      return "suprimentos";
    case "financeiro":
      return "financeiro";
    case "orcamentos":
      return "orcamentos";
    case "comercial":
      return "comercial";
    case "gerencial":
      return "gerencial";
    case "chaoDeObra":
      return "chaoDeObra";
    case "solicitacaoPagamento":
      return "solicitacaoPagamento";
    case "tarefas":
      return "tarefas";
    case "locacoes":
      return "locacoes";
    case "manutencao":
      return "manutencao";
    case "folhaPagamentos":
      return "folhaPagamentos";
    case "genteGestao":
      return "genteGestao";
    default:
      return null;
  }
}

function moduleRoute(id: string): string | null {
  switch (id) {
    case "suprimentos":
      return ROUTES.SUPRIMENTOS;
    case "financeiro":
      return ROUTES.FINANCEIRO;
    case "orcamentos":
      return ROUTES.ORCAMENTO;
    case "comercial":
      return ROUTES.COMERCIAL;
    case "gerencial":
      return ROUTES.GERENCIAL;
    case "chaoDeObra":
      return ROUTES.CHAO_DE_OBRA;
    case "solicitacaoPagamento":
      return ROUTES.SOLICITACAO_PAGAMENTO;
    case "tarefas":
      return ROUTES.TAREFAS;
    case "locacoes":
      return ROUTES.LOCACOES;
    case "manutencao":
      return ROUTES.MANUTENCAO;
    case "folhaPagamentos":
      return ROUTES.FOLHA_PAGAMENTOS;
    case "genteGestao":
      return ROUTES.GENTE_GESTAO;
    default:
      return null;
  }
}

/* ─── Decoração de pontos ────────────────────────────────────────────────── */

function DotsDecoration({ className }: { className?: string }) {
  return (
    <div
      className={cn("grid gap-[5px] opacity-40", className)}
      style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
    >
      {Array.from({ length: 25 }).map((_, i) => (
        <div key={i} className="w-[5px] h-[5px] rounded-full bg-blue-300" />
      ))}
    </div>
  );
}

/* ─── Card de módulo ─────────────────────────────────────────────────────── */

function ModuleCard({ module, onClick }: { module: Module; onClick: () => void }) {
  const { Icon, gradientFrom, gradientTo, label, description } = module;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl overflow-hidden shadow-md cursor-pointer hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 select-none min-w-0 h-full min-h-0 w-full flex flex-col"
    >
      {/* Seção gradiente — cresce para preencher a altura do card */}
      <div
        className="flex-1 min-h-0 flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        }}
      >
        <div className="w-[clamp(3rem,38%,5.5rem)] aspect-square rounded-full bg-white flex items-center justify-center shadow-sm">
          <Icon
            strokeWidth={1.8}
            className="w-[clamp(1.25rem,55%,2.25rem)] h-[clamp(1.25rem,55%,2.25rem)]"
            style={{ color: gradientFrom }}
          />
        </div>
      </div>

      {/* Seção de texto */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-2.5 sm:py-3 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold text-slate-800 text-sm sm:text-base leading-tight line-clamp-2">{label}</p>
          <p className="text-slate-500 text-xs sm:text-sm mt-0.5 whitespace-pre-line leading-snug line-clamp-2">
            {description}
          </p>
        </div>

        <button
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ color: gradientFrom }}
          aria-label={`Acessar ${label}`}
        >
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

/** Colunas máximas conforme a largura da tela */
function useMaxGridCols(): number {
  const [maxCols, setMaxCols] = useState(4);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setMaxCols(2);
      else if (w < 1024) setMaxCols(3);
      else setMaxCols(4);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return maxCols;
}

/**
 * Grade larga, com poucas linhas, para os cards continuarem grandes.
 * Prefere linhas completas para o conjunto inteiro de módulos caber na tela.
 */
function computeMenuGrid(count: number, maxCols: number): { cols: number; rows: number } {
  if (count <= 0) return { cols: 1, rows: 1 };
  if (count === 1) return { cols: 1, rows: 1 };

  const limit = Math.min(count, maxCols);
  const minCols = count <= 2 ? count : Math.min(limit, 2);
  let bestCols = minCols;
  let bestScore = Infinity;

  for (let cols = minCols; cols <= limit; cols++) {
    const rows = Math.ceil(count / cols);
    const lastRow = count % cols === 0 ? cols : count % cols;
    const lastRowEmpty = cols - lastRow;
    const score = rows * 100 + lastRowEmpty * 12 - cols;
    if (score < bestScore) {
      bestScore = score;
      bestCols = cols;
    }
  }

  return { cols: bestCols, rows: Math.ceil(count / bestCols) };
}

/* ─── Página principal ───────────────────────────────────────────────────── */

export function HomePage() {
  const navigate = useNavigate();
  const { can, isAdmin } = useAppAccess();
  const { user } = useAuth();
  const maxGridCols = useMaxGridCols();

  useEffect(() => {
    if (
      isOperadorOnlyUser(can, {
        locacoesOperador: user?.locacoesOperador,
        role: user?.role,
      })
    ) {
      navigate(ROUTES.OPERADOR, { replace: true });
      return;
    }
    if (
      isMarketingOnlyUser(can, {
        locacoesOperador: user?.locacoesOperador,
        role: user?.role,
      })
    ) {
      navigate(ROUTES.MARKETING, { replace: true });
    }
  }, [can, user, navigate]);

  const visibleModules = MODULES.filter((mod) => {
    if (mod.id === "solicitacaoPagamento") {
      return canAccessSolicitacaoPagamentoModule(can);
    }
    if (mod.id === "locacoes") {
      return canAccessLocacoesModule(can, user?.locacoesOperador);
    }
    const k = moduleAccessKey(mod.id);
    return k ? can(k) : false;
  });

  const menuGrid = useMemo(
    () => computeMenuGrid(visibleModules.length, maxGridCols),
    [visibleModules.length, maxGridCols],
  );

  return (
    <div
      className="h-dvh overflow-hidden flex flex-col relative w-full max-w-[100vw] box-border"
      style={{ backgroundColor: "#f0f5ff" }}
    >
      {/* Decoração pontos */}
      <DotsDecoration className="absolute top-4 right-6 hidden xl:grid pointer-events-none" />
      <DotsDecoration className="absolute bottom-4 left-6 hidden xl:grid pointer-events-none" />

      {/* Cabeçalho */}
      <header className="flex-shrink-0 text-center px-4 pt-4 pb-3 sm:pt-5 sm:pb-4">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">
          Bem-vindo!
        </h1>
        <p className="text-slate-500 text-sm sm:text-base mt-1">
          Selecione um módulo para acessar
        </p>
        <div
          className="w-12 h-[3px] rounded-full mx-auto mt-3"
          style={{ background: "linear-gradient(to right, #6366f1, #8b5cf6)" }}
        />
      </header>

      {/* Grade de módulos — cards grandes; rola se não couber na tela */}
      <div className="flex-1 min-h-0 w-full overflow-y-auto px-3 sm:px-4 md:px-5 pb-3">
        {visibleModules.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-8">
            Nenhum módulo liberado para seu usuário. Peça ao administrador para ajustar seu grupo de acesso.
          </p>
        ) : (
          <div
            className="grid h-full w-full gap-2 sm:gap-3 md:gap-4"
            style={{
              gridTemplateColumns: `repeat(${menuGrid.cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${menuGrid.rows}, minmax(7.5rem, 1fr))`,
            }}
          >
            {visibleModules.map((mod) => (
              <ModuleCard
                key={mod.id}
                module={mod}
                onClick={() => {
                  const r = moduleRoute(mod.id);
                  navigate(r ?? ROUTES.HOME);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rodapé */}
      <div className="flex-shrink-0 pb-3 pt-1 flex flex-col items-center gap-1 text-slate-400 text-[10px] sm:text-xs">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3 h-3" />
          <span>Sistema SEMOG • Gestão integrada para resultados</span>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_ACESSO)}
            className="text-brand-600 hover:text-brand-700 font-medium underline-offset-2 hover:underline"
          >
            Gerenciar usuários e grupos
          </button>
        )}
      </div>
    </div>
  );
}
